import { useEffect, useState } from 'react';
import { Plus, Sparkles } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import IdentitySelector from '@/components/ai/IdentitySelector';
import { useAuth } from '@/contexts/AuthContext';

const DESCRIPTION_LIMIT = 500;

export interface NewWorkflowDraft {
  name: string;
  description: string;
  trigger_type: string;
  acts_as_user_ids: string[];
  allowed_sender_ids: string[] | null;
  admission_scope: 'tenant' | 'campaigns';
}

const TRIGGER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'trigger_manual', label: 'Manuale' },
  { value: 'trigger_scheduled', label: 'Schedulato' },
  { value: 'trigger_webhook', label: 'Webhook' },
  { value: 'trigger_whatsapp_received', label: 'WhatsApp ricevuto' },
  { value: 'trigger_sms_received', label: 'SMS ricevuto' },
  { value: 'trigger_call_answered', label: 'Chiamata risposta' },
  { value: 'trigger_call_hangup', label: 'Chiamata terminata' },
  { value: 'trigger_voicemail_left', label: 'Voicemail ricevuta' },
  { value: 'trigger_lead_created', label: 'Lead creato' },
  { value: 'trigger_lead_stage_changed', label: 'Lead cambio stato' },
  { value: 'trigger_customer_created', label: 'Cliente creato' },
  { value: 'trigger_form_submitted', label: 'Form inviato' },
  { value: 'trigger_tag_added', label: 'Tag aggiunto' },
];

interface NewWorkflowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (draft: NewWorkflowDraft) => void;
}

export function NewWorkflowDialog({ open, onOpenChange, onConfirm }: NewWorkflowDialogProps) {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState('trigger_manual');
  const [actsAsUserIds, setActsAsUserIds] = useState<string[]>([]);
  const [allowedSenderIds, setAllowedSenderIds] = useState<string[] | null>(null);
  const [scope, setScope] = useState<'tenant' | 'campaigns'>('campaigns');

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setTriggerType('trigger_manual');
      setActsAsUserIds([]);
      setAllowedSenderIds(null);
      setScope('campaigns');
    }
  }, [open]);

  const canConfirm = name.trim().length > 0;

  const handleConfirm = () => {
    if (!canConfirm) return;
    const draft: NewWorkflowDraft = {
      name: name.trim(),
      description: description.trim(),
      trigger_type: triggerType,
      acts_as_user_ids: actsAsUserIds,
      allowed_sender_ids: allowedSenderIds,
      admission_scope: scope,
    };
    console.log('[WB-EntryFlow] NewWorkflowDialog → confirm', draft);
    onConfirm(draft);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Nuovo workflow
          </DialogTitle>
          <DialogDescription>
            Dai un nome al workflow e scegli il trigger iniziale.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="new-wf-name">
              Nome <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-wf-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Nuovo lead da WhatsApp"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && canConfirm) {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="new-wf-desc">Descrizione</Label>
              <span className="text-[11px] text-muted-foreground">
                {description.length}/{DESCRIPTION_LIMIT}
              </span>
            </div>
            <Textarea
              id="new-wf-desc"
              value={description}
              onChange={(e) => {
                if (e.target.value.length <= DESCRIPTION_LIMIT) {
                  setDescription(e.target.value);
                }
              }}
              placeholder="Cosa fa questo workflow? (opzionale)"
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-wf-trigger">Trigger iniziale</Label>
            <Select value={triggerType} onValueChange={setTriggerType}>
              <SelectTrigger id="new-wf-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIGGER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Potrai cambiarlo successivamente dal canvas.
            </p>
          </div>

          <IdentitySelector
            value={{ acts_as_user_ids: actsAsUserIds, allowed_sender_ids: allowedSenderIds }}
            canTenantWide={isAdminUser}
            onChange={(next) => {
              setActsAsUserIds(next.acts_as_user_ids);
              setAllowedSenderIds(next.allowed_sender_ids);
            }}
          />
          <div className="space-y-1.5">
            <Label htmlFor="new-wf-scope">Ambito esplicito</Label>
            <Select value={scope} onValueChange={value => { if (value === 'tenant' || value === 'campaigns') setScope(value); }}>
              <SelectTrigger id="new-wf-scope"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="campaigns">Solo campagne collegate</SelectItem><SelectItem value="tenant">Autonomo tenant</SelectItem></SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Creare o salvare non collega automaticamente alcuna campagna. La compatibilità del grafo viene verificata dal server.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Crea e apri
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
